import { formatCents, type Cents } from "../domain/money";
import type { ProjectFinancialDashboard, AccountingBucketGroup } from "../db/projectFinancialsService";
import type { ProjectedBasisLine, RollupRow } from "../engine/projectFinancials";
import { BUCKET_ORDER } from "../engine/projectFinancials";
import type { ReportIdentity, ReportModel, ReportRow, ReportSection, ReportValue } from "./reportModel";

/**
 * PROJECT FINANCIAL REPORT MODELS (Phase 12 §41C, extended in Phase 13).
 *
 * Reports are built from STRUCTURED DATA, never from HTML strings scattered
 * through the UI. The desktop renders these models as panels, the printed
 * document paginates the same models, and the exports serialize the same
 * numbers — with no second source of truth and no report-only maths.
 *
 * Pure and portable — no React, no DOM, no database.
 */

export type { ReportIdentity, ReportModel, ReportRow, ReportSection, ReportValue } from "./reportModel";

const money = (cents: Cents): ReportValue => ({ kind: "money", cents });
const fmtMoney = (cents: Cents): string => formatCents(cents);
const text = (value: string): ReportValue => ({ kind: "text", text: value });
const percent = (pctX100: number | null): ReportValue => ({ kind: "percent", pctX100 });

/**
 * A new-construction project has no "rehab" — the very same number is its
 * construction budget (spec §41A, project types from Phase 9). This is a
 * vocabulary switch only: never a second calculation.
 */
function workNoun(d: ProjectFinancialDashboard): string {
  return d.projectType === "new_construction" ? "construction" : "rehab";
}

function WorkNoun(d: ProjectFinancialDashboard): string {
  const noun = workNoun(d);
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

/**
 * PROJECT FINANCIAL SUMMARY — the dashboard as a report: what we expected,
 * authorized, currently expect, have spent, and will likely spend.
 */
const perSqftValue = (cents: Cents | null): ReportValue =>
  cents === null ? { kind: "text", text: "—" } : { kind: "text", text: `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` };

export function projectFinancialSummaryReport(
  d: ProjectFinancialDashboard,
  identity?: ReportIdentity,
  /** $/sq ft benchmarks (spec §77L) — present only when the area is known. */
  sqft?: import("../engine/calibration").ProjectSqftMetrics & { strategy: "flip" | "rental" },
  /**
   * Spec §91-31: how much of the renovation is closed out. Counts only —
   * the calibration arithmetic, the historical sample and the exclusion
   * controls belong on screen, not on a printed page, and none of this
   * reaches a contractor document.
   */
  closeout?: { completedAt: string | null; completeCount: number; itemCount: number; learnedCount: number },
): ReportModel {
  const rehabRows: ReportRow[] = [];
  if (d.rehab.hasBaseline) {
    rehabRows.push({ label: "Original baseline", values: [money(d.rehab.baselineCents)] });
    if (d.rehab.approvedChangeCents !== 0) {
      rehabRows.push({ label: "Approved change orders (budget funded)", values: [money(d.rehab.approvedChangeCents)] });
    }
    if (d.rehab.contingencyFundedCents !== 0) {
      rehabRows.push({
        label: "Approved change orders (from contingency)",
        note: "Already inside the frozen contingency, so the authorized total does not move.",
        values: [money(d.rehab.contingencyFundedCents)],
      });
    }
    rehabRows.push({ label: `Authorized ${workNoun(d)} budget`, values: [money(d.rehab.authorizedCents)], emphasis: "subtotal" });
  } else {
    rehabRows.push({
      label: "No baseline yet",
      note: "Nothing has been formally approved, so the working estimate is the only expectation.",
      values: [{ kind: "empty" }],
    });
  }
  rehabRows.push({ label: "Calculated scope + contingency", note: "Before selected quotes or category authorizations. See Renovation budget for the current category planning basis.", values: [money(d.rehab.currentWorkingCents)] });
  rehabRows.push({
    label: `Projected final ${workNoun(d)}`,
    note: d.rehab.projectionIncomplete
      ? "Actual where comparable costs are complete; remaining or unclassified costs still require review."
      : `Every basis is complete: this is the final ${workNoun(d)} cost.`,
    values: [money(d.rehab.projectedFinalCents)],
  });
  rehabRows.push({ label: `Actual ${workNoun(d)} recorded to date`, values: [money(d.rehab.actualToDateCents)] });
  if (d.rehab.hasBaseline) {
    rehabRows.push({
      label: "Projected vs authorized",
      values: [money(d.rehab.projectedVarianceCents), percent(d.rehab.projectedVariancePctX100)],
      emphasis: d.rehab.projectedVarianceCents > 0 ? "warning" : undefined,
    });
    rehabRows.push({
      label: "Unapproved planning difference",
      note: "Working estimate beyond what was authorized — a planning question, not an overrun.",
      values: [money(d.rehab.unapprovedPlanningCents)],
    });
    if (d.rehab.initialContingencyCents > 0) {
      rehabRows.push({
        label: "Contingency remaining",
        note: `Of ${fmt(d.rehab.initialContingencyCents)} frozen at approval.`,
        values: [money(d.rehab.remainingContingencyCents)],
      });
    }
  }

  const costRows: ReportRow[] = [
    { label: "Purchase price", values: [money(d.cost.purchasePriceCents)] },
    { label: "Acquisition costs", values: [money(d.cost.acquisitionCostsCents)] },
    {
      label: d.cost.rehabIsProjected ? `${WorkNoun(d)} (projected final)` : `${WorkNoun(d)} (final)`,
      values: [money(d.cost.rehabCents)],
    },
    { label: "Holding costs", values: [money(d.cost.holdingCents)] },
    {
      label:
        d.financingOutlook.projectedFutureCents > 0
          ? "Financing costs (recorded + projected)"
          : "Financing costs",
      note:
        d.financingOutlook.projectedFutureCents > 0
          ? `${fmtMoney(d.financingOutlook.actualToDateCents)} recorded; ${fmtMoney(d.financingOutlook.projectedFutureCents)} of future interest and fees projected from the loan terms. Lender principal is funding, never a cost.`
          : undefined,
      values: [money(d.cost.financingCents)],
    },
    { label: "Selling costs", values: [money(d.cost.sellingCents)] },
    { label: "Other", values: [money(d.cost.otherCents)] },
    { label: "Total project cost", values: [money(d.cost.totalCents)], emphasis: "total" },
  ];

  const outcomeRows: ReportRow[] = [];
  // Spec §74Y: a RENTAL's stabilized value is NEVER presented as profit.
  // Until the property actually sells, its outcome section speaks in
  // rental words — value is equity ground, not revenue.
  const rentalForecast = d.strategy === "rental" && !(d.sale && d.sale.grossSalePriceCents > 0);
  if (d.sale && d.sale.grossSalePriceCents > 0) {
    outcomeRows.push({ label: "Gross sale price", values: [money(d.sale.grossSalePriceCents)] });
    outcomeRows.push({
      label: "Selling deductions",
      note: d.sale.source === "accounting" ? "Taken from the accounting records." : "From the sale record.",
      values: [money(-d.sale.sellingDeductionsCents)],
    });
    outcomeRows.push({ label: "Net proceeds", values: [money(d.sale.netProceedsCents)], emphasis: "subtotal" });
  } else if (rentalForecast) {
    outcomeRows.push({
      label: "Estimated property value",
      note: "A rental's value is equity, not realized profit. NOI, cash flow, DSCR and cash-on-cash live in Financing & timeline.",
      values: [money(d.expectedArvCents ?? 0)],
    });
  } else {
    outcomeRows.push({
      label: "Expected ARV (sale pending)",
      note: "No sale recorded yet, so profit below is a forecast.",
      values: [money(d.expectedArvCents ?? 0)],
    });
  }
  if (d.baselineExpectedArvCents !== null) {
    outcomeRows.push({
      label: "Expected ARV at baseline",
      note: "Frozen when the baseline was approved; never rewritten.",
      values: [money(d.baselineExpectedArvCents)],
    });
  }
  if (!rentalForecast) {
    outcomeRows.push({
      label: d.outcome.isForecast ? "Projected profit" : "Net profit",
      values: [money(d.outcome.netProfitCents)],
      emphasis: "total",
    });
    outcomeRows.push({
      label: d.outcome.isForecast ? "Projected ROI" : "ROI",
      note: d.outcome.roiFormula,
      values: [percent(d.outcome.roiPctX100)],
    });
    if (d.outcome.annualizedPctX100 !== null) {
      outcomeRows.push({
        label: "Annualized return",
        note: d.outcome.annualizedFormula ?? "",
        values: [percent(d.outcome.annualizedPctX100)],
      });
    }
  } else {
    outcomeRows.push({
      label: "Projected total project cost",
      note: "What executing the project costs; the rental's return is measured by cash flow and equity, not a sale forecast.",
      values: [money(d.cost.totalCents)],
      emphasis: "total",
    });
  }
  if (d.outcome.holdingDays !== null) {
    outcomeRows.push({ label: "Days held", values: [{ kind: "count", value: d.outcome.holdingDays }] });
  }

  const disclosures = [
    "Actual figures come from the recorded transactions (purchases, expenses, mileage, returns and labor settlements); nothing here is a stored total.",
    d.committedCostSeam,
  ];
  if (d.rehab.projectionIncomplete) {
    disclosures.push(
      `Some work or cost classifications remain incomplete, so the projected final ${workNoun(d)} still requires review and no final variance is claimed.`,
    );
  }
  if (d.sale?.doubleCountWarning) disclosures.push(d.sale.doubleCountWarning);
  if (d.outcome.isForecast && !rentalForecast) {
    disclosures.push("Profit and ROI are a FORECAST using the current Expected ARV, not a result.");
  }
  if (d.renovationBasisDeltaCents !== 0) {
    disclosures.push(
      `The projected ${workNoun(d)} uses the CURRENT category planning basis — selected contractor quotes and authorized category budgets adjust the calculated estimate by ${fmt(d.renovationBasisDeltaCents)} (never counted twice).`,
    );
  }

  return {
    id: "project-financial-summary",
    title: "Project financial summary",
    subtitle: `JOB ${d.jobNumber} — ${d.projectName}`,
    identity,
    sections: [
      { title: `${WorkNoun(d)} budget control`, columns: ["Amount", "%"], rows: rehabRows },
      { title: "Total project cost", columns: ["Amount"], rows: costRows },
      { title: d.outcome.isForecast ? "Projected outcome" : "Outcome", columns: ["Amount"], rows: outcomeRows },
      ...(closeout && closeout.itemCount > 0
        ? [
            {
              title: "Renovation completion",
              columns: ["Count"],
              note:
                closeout.completedAt === null
                  ? "The renovation has not been closed out yet."
                  : `Closed out ${closeout.completedAt.slice(0, 10)}.`,
              rows: [
                { label: "Work items", values: [{ kind: "text", text: String(closeout.itemCount) } as const] },
                { label: "Marked complete", values: [{ kind: "text", text: String(closeout.completeCount) } as const] },
                {
                  label: "Counting towards future estimates",
                  note: "Finished work with a reconciled cost and a known quantity.",
                  values: [{ kind: "text", text: String(closeout.learnedCount) } as const],
                },
              ],
            } satisfies ReportSection,
          ]
        : []),
      // Spec §77L/T: owner economics on the owner's own report — never on a
      // contractor document. Absent entirely when the area is unknown.
      ...(sqft && sqft.livingAreaSqftX100 !== null
        ? [
            {
              title: "Per square foot",
              columns: ["$ / sq ft"],
              note: `${(sqft.livingAreaSqftX100 / 100).toLocaleString("en-US")} sq ft living area. Renovation uses the current planning basis (calculated → quoted → authorized).`,
              rows: [
                { label: "Purchase price", values: [perSqftValue(sqft.purchasePerSqft)] },
                { label: d.projectType === "new_construction" ? "Construction" : "Renovation", values: [perSqftValue(sqft.renovationPerSqft)] },
                {
                  label: sqft.strategy === "rental" ? "Estimated property value" : "Expected ARV",
                  values: [perSqftValue(sqft.valuePerSqft)],
                },
                ...(sqft.salePerSqft !== null
                  ? [{ label: "Sale price", values: [perSqftValue(sqft.salePerSqft)] }]
                  : []),
              ],
            } satisfies ReportSection,
          ]
        : []),
    ],
    disclosures,
    generatedAt: new Date().toISOString(),
  };
}

/**
 * THE RENOVATION BUDGET BY CATEGORY (spec §74K/Z/AA).
 *
 * How a real renovation is budgeted, quoted, paid and reviewed: one row
 * per trade category with the number currently driving it, what has been
 * spent, what remains and why it varied — the detailed estimate stays in
 * the estimating reports where it belongs.
 */
export function renovationBudgetReport(
  d: ProjectFinancialDashboard,
  budget: import("../engine/categoryBudget").CategoryBudgetModel,
  identity?: ReportIdentity,
): ReportModel {
  const basisWord: Record<string, string> = {
    calculated: "Calculated estimate",
    quoted: "Selected quote",
    authorized: "Authorized budget",
    actual: "Actual (complete)",
  };
  const rows: ReportRow[] = budget.rows.flatMap((row): ReportRow[] => [
    {
      label: row.displayName,
      note:
        [
          basisWord[row.basisSource],
          row.quotes.length > 0 ? `quoted by ${row.quotes.map((q) => q.contractor).join(", ")}` : null,
          row.varianceJustification ? `Variance: ${row.varianceJustification}` : null,
        ]
          .filter(Boolean)
          .join(" · ") || undefined,
      /* THE THREE CHECKPOINTS, ON PAPER (spec §84-28): what we estimated,
       * what we were quoted, what we spent — then the operational budget.
       * A category nobody has quoted prints a dash, never $0. */
      values: [
        money(row.calculatedCents),
        row.quoteCents === null
          ? text("—")
          : row.quoteCoverage === "partial"
            ? text(`${formatCents(row.quoteCents)} (partial)`)
            : money(row.quoteCents),
        money(row.actualCents),
        money(row.currentBasisCents),
        money(row.remainingCents),
      ],
      emphasis: !row.complete && row.actualCents > row.currentBasisCents ? "warning" : undefined,
    },
    // MANAGEMENT WORK ITEMS (spec §78S): the concise breakdown a project is
    // managed by — never the room-by-room detail, which stays in the
    // estimate reports. Item figures are the CALCULATED estimate; a
    // category-level quote or authorization is never apportioned.
    ...row.management
      .filter((item) => item.amountCents !== 0 || item.actualCents !== 0)
      .flatMap((item): ReportRow[] => {
        // §78's management identity can contain different finish recipes.
        // Its first feature's name must not describe the whole mixed price.
        // Keep the group's quote intact; each child's calculated/actual
        // amount already comes from the engine, never an apportionment.
        const mixedFlooring = row.categoryId === "flooring" && new Set(item.details.map(detail => detail.name.trim().toLowerCase())).size > 1;
        const heading: ReportRow = {
          label: mixedFlooring && item.label === item.generatedLabel ? "Flooring — different specifications" : item.label,
          depth: 1,
          values: [
            money(item.amountCents),
            // §84-10: only a quote covering this item ALONE belongs here.
            item.quoteCents === null ? text("") : money(item.quoteCents),
            item.actualCents !== 0 ? money(item.actualCents) : text(""),
            text(""),
            text(""),
          ],
        };
        return !mixedFlooring ? [heading] : [heading, ...item.details.map((detail): ReportRow => ({
          label: detail.name,
          depth: 2,
          values: [money(detail.calculatedCents), text(""), detail.actualCents !== 0 ? money(detail.actualCents) : text(""), text(""), text("")],
          source: detail.featureId ? { kind: "feature", id: detail.featureId } : undefined,
        }))];
      }),
  ]);
  rows.push({
    label: "Renovation budget",
    values: [
      money(budget.totals.calculatedCents),
      budget.totals.quoteCents === null ? text("—") : money(budget.totals.quoteCents),
      money(budget.totals.actualCents),
      money(budget.totals.currentBasisCents),
      money(budget.totals.remainingCents),
    ],
    emphasis: "total",
  });
  /* A QUOTE TOTAL IS NOT A QUOTED RENOVATION (spec §84-13). */
  if (budget.totals.quoteCents !== null && budget.totals.quoteCoveragePctX100 !== null) {
    rows.push({
      label: "Quote coverage",
      note: `Selected quotes cover ${formatCents(budget.totals.quotedCalculatedCents)} of ${formatCents(budget.totals.calculatedCents)} proposed scope${budget.totals.quoteCoverage === "full" ? "" : " — the rest is still our own estimate"}.`,
      values: [text(""), text(`${(budget.totals.quoteCoveragePctX100 / 100).toFixed(0)}%`), text(""), text(""), text("")],
    });
  }
  return {
    id: "renovation-budget",
    title: "Renovation budget",
    subtitle: `JOB ${d.jobNumber} — ${d.projectName}`,
    identity,
    sections: [
      {
        title: "By category",
        columns: ["Proposed", "Quote", "Actual", "Current budget", "Remaining"],
        rows,
      },
    ],
    disclosures: [
      "Generic material and labor prices are preliminary estimating assumptions. Unrecorded existing conditions, structural suitability, plumbing or electrical relocation, hidden damage and code requirements need inspection or a specific scope decision; this budget does not invent work or allowances for them.",
      "Proposed is what Berrier Reno calculated BEFORE any quote and never changes when a quote is selected; Quote is the selected contractor quote covering that scope, counted once and shown at the level where it is actually known; Actual is the reconciled accounting. A dash means nobody has quoted that work — it is not a quote of $0.",
      "Each category's current budget is the strongest planning truth it has: calculated estimate → selected contractor quote → authorized budget; actual money progressively supersedes it and a finished category reports variance instead of a remainder.",
      "The detailed line-by-line calculation remains in Scope & Estimate and the estimate reports — this is how the project is MANAGED, not how it was calculated.",
      "A selected quote is a proposed price, never an actual expense; nothing here is a stored total.",
      "Indented work items are grouped from the detailed estimate and show the CALCULATED figure; a category-level quote or authorization stands for the whole category and is never apportioned across items.",
    ],
    generatedAt: new Date().toISOString(),
  };
}

/** ITEM-LEVEL FINANCIAL CONTROL — baseline / authorized / current / actual. */
export function financialControlReport(
  d: ProjectFinancialDashboard,
  rows: RollupRow[],
  dimension: string,
  identity?: ReportIdentity,
): ReportModel {
  return {
    id: "financial-control",
    title: "Financial control detail",
    subtitle: `JOB ${d.jobNumber} — by ${dimension}`,
    identity,
    sections: [
      {
        title: `By ${dimension}`,
        columns: ["Current", "Actual", "Projected final", "Variance"],
        rows: rows.map((row) => ({
          label: row.label,
          note: row.incomplete ? "Still in progress — the projection is not final." : undefined,
          values: [money(row.proposedCents), money(row.actualCents), money(row.projectedCents), money(row.varianceCents)],
        })),
      },
    ],
    disclosures: [
      "Variance compares the projected final with the current estimate. Phase 9C comparability rules still apply: an unfinished item never claims a final variance.",
    ],
    generatedAt: new Date().toISOString(),
  };
}

/** ACCOUNTANT DETAIL — where every dollar went, by bucket and category. */
export function accountingDetailReport(
  d: ProjectFinancialDashboard,
  groups: AccountingBucketGroup[],
  identity?: ReportIdentity,
): ReportModel {
  const sections: ReportSection[] = groups
    .sort((a, b) => BUCKET_ORDER.indexOf(a.bucket) - BUCKET_ORDER.indexOf(b.bucket))
    .map((group) => ({
      title: `${bucketLabel(group.bucket)} — ${fmt(group.totalCents)}`,
      columns: ["Amount", "Tax", "Total", "Document"],
      rows: group.categories.flatMap((category) => [
        { label: category.categoryName, values: [{ kind: "empty" as const }, { kind: "empty" as const }, money(category.totalCents), { kind: "empty" as const }], emphasis: "subtotal" as const },
        ...category.rows.map((row) => ({
          label: `${row.date} · ${row.vendor || "—"} · ${row.description}`,
          note: [row.costBasis ? `${row.costBasis} basis` : null, row.isReturn ? "return / refund" : null]
            .filter(Boolean)
            .join(" · ") || undefined,
          depth: 1,
          values: [money(row.amountCents), money(row.taxCents), money(row.totalCents), text(documentLabel(row.documentStatus))],
        })),
      ]),
    }));
  return {
    id: "accounting-detail",
    title: "Tax / accounting records",
    subtitle: `JOB ${d.jobNumber} — ${d.projectName}`,
    identity,
    sections,
    disclosures: [
      "These are the project's recorded facts, organized by financial bucket and category. Berrier Reno does not state tax treatment or deductibility — that is your accountant's decision.",
      "Every row traces to a purchase, expense or mileage record with its source document status.",
    ],
    generatedAt: new Date().toISOString(),
  };
}

/** MILEAGE — date, miles, frozen rate, amount. */
export function mileageReport(
  d: ProjectFinancialDashboard,
  entries: { date: string; purpose: string; startLocation: string; destination: string; milesX10: number; rateCentsPerMile: Cents; amountCents: Cents; bucket: string; notes: string }[],
): ReportModel {
  return {
    id: "mileage",
    title: "Mileage",
    subtitle: `JOB ${d.jobNumber} — ${d.projectName}`,
    sections: [
      {
        title: "Trips",
        columns: ["Miles", "Rate", "Amount"],
        rows: [
          ...entries.map((entry) => ({
            label: `${entry.date} · ${entry.purpose}`,
            note: [entry.startLocation, entry.destination].filter(Boolean).join(" → ") || undefined,
            values: [
              { kind: "count" as const, value: entry.milesX10 / 10 },
              money(entry.rateCentsPerMile),
              money(entry.amountCents),
            ],
          })),
          {
            label: "Total",
            values: [
              { kind: "count", value: entries.reduce((s, e) => s + e.milesX10, 0) / 10 },
              { kind: "empty" },
              money(entries.reduce((s, e) => s + e.amountCents, 0)),
            ],
            emphasis: "total",
          },
        ],
      },
    ],
    disclosures: ["Each trip stores the rate that applied when it was recorded; changing the rate later never rewrites history."],
    generatedAt: new Date().toISOString(),
  };
}

/** CLOSEOUT — the whole project on one page. */
export function closeoutReport(d: ProjectFinancialDashboard): ReportModel {
  const summary = projectFinancialSummaryReport(d);
  const readiness: ReportRow[] = d.warnings.length === 0
    ? [{ label: "Nothing outstanding", values: [{ kind: "empty" }] }]
    : d.warnings.map((w) => ({
        label: w.label,
        note: w.detail,
        values: [{ kind: "count", value: w.count }],
        emphasis: "warning" as const,
      }));
  return {
    id: "closeout",
    title: "Project closeout",
    subtitle: `JOB ${d.jobNumber} — ${d.projectName}`,
    sections: [
      {
        title: "Project",
        columns: ["Value"],
        rows: [
          { label: "Job number", values: [text(d.jobNumber)] },
          { label: "Project type", values: [text(d.projectType === "new_construction" ? "New construction" : "Renovation")] },
          { label: "Purchase date", values: [text(d.purchaseDate ?? "—")] },
          { label: "Sale date", values: [text(d.sale?.saleDate ?? "—")] },
          { label: "Status", values: [text(d.statusId)] },
        ],
      },
      ...summary.sections,
      { title: "Outstanding at closeout", columns: ["Count"], rows: readiness },
    ],
    disclosures: summary.disclosures,
    generatedAt: new Date().toISOString(),
  };
}

function bucketLabel(bucket: string): string {
  return bucket.charAt(0).toUpperCase() + bucket.slice(1);
}

function documentLabel(status: string): string {
  switch (status) {
    case "attached": return "Receipt attached";
    case "purchase": return "On the purchase";
    case "not-required": return "—";
    default: return "No source document";
  }
}

function fmt(cents: Cents): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}$${Math.floor(abs / 100).toLocaleString("en-US")}.${String(abs % 100).padStart(2, "0")}`;
}

/* ------------------------------------------------------------------ */
/* Phase 13 — item-level control and the flagship closeout             */
/* ------------------------------------------------------------------ */

export interface ControlItemRow {
  key: string;
  label: string;
  note?: string;
  featureId: string | null;
  /** Frozen at approval; null when the item was not in the baseline. */
  baselineCents: Cents | null;
  /** Baseline plus approved change orders touching this item. */
  authorizedCents: Cents | null;
  proposedCents: Cents;
  actualCents: Cents;
  projectedCents: Cents;
  varianceCents: Cents;
  incomplete: boolean;
  statusLabel: string;
}

export type ControlLayout = "default" | "detailed";

/**
 * ITEM-LEVEL FINANCIAL CONTROL (Phase 13, spec §42H).
 *
 * The default layout stays narrow enough to print: what was authorized,
 * what it will cost, what has been spent, and the difference. The detailed
 * layout adds the baseline and the current proposal for a full audit trail.
 *
 * Comparability is preserved: an item whose actuals are incomplete NEVER
 * prints a final variance — it prints its state instead.
 */
export function itemFinancialControlReport(input: {
  dashboard: ProjectFinancialDashboard;
  items: ControlItemRow[];
  dimension: string;
  layout: ControlLayout;
  identity?: ReportIdentity;
  /** Authorized money that belongs to no item, so the total is explicable. */
  contingencyCents?: Cents;
  generatedAt?: string;
}): ReportModel {
  const { dashboard: d, items, layout } = input;
  const detailed = layout === "detailed";
  const columns = detailed
    ? ["Baseline", "Authorized", "Current", "Actual", "Projected", "Variance", "Status"]
    : ["Authorized", "Projected", "Actual", "Variance", "Status"];

  const rows: ReportRow[] = items.map((item) => ({
    label: item.label,
    note: item.note,
    source: item.featureId ? ({ kind: "feature", id: item.featureId } as const) : undefined,
    values: detailed
      ? [
          item.baselineCents === null ? ({ kind: "empty" } as ReportValue) : money(item.baselineCents),
          item.authorizedCents === null ? ({ kind: "empty" } as ReportValue) : money(item.authorizedCents),
          money(item.proposedCents),
          money(item.actualCents),
          money(item.projectedCents),
          item.incomplete ? ({ kind: "empty" } as ReportValue) : money(item.varianceCents),
          text(item.statusLabel),
        ]
      : [
          item.authorizedCents === null ? ({ kind: "empty" } as ReportValue) : money(item.authorizedCents),
          money(item.projectedCents),
          money(item.actualCents),
          item.incomplete ? ({ kind: "empty" } as ReportValue) : money(item.varianceCents),
          text(item.statusLabel),
        ],
    emphasis: !item.incomplete && item.varianceCents > 0 ? ("warning" as const) : undefined,
  }));

  const totals = items.reduce(
    (acc, item) => ({
      baseline: acc.baseline + (item.baselineCents ?? 0),
      authorized: acc.authorized + (item.authorizedCents ?? 0),
      proposed: acc.proposed + item.proposedCents,
      actual: acc.actual + item.actualCents,
      projected: acc.projected + item.projectedCents,
      variance: acc.variance + item.varianceCents,
    }),
    { baseline: 0, authorized: 0, proposed: 0, actual: 0, projected: 0, variance: 0 },
  );
  const anyIncomplete = items.some((item) => item.incomplete);
  rows.push({
    label: "Total",
    emphasis: "total",
    values: detailed
      ? [
          money(totals.baseline),
          money(totals.authorized),
          money(totals.proposed),
          money(totals.actual),
          money(totals.projected),
          anyIncomplete ? { kind: "empty" } : money(totals.variance),
          text(anyIncomplete ? "Actuals incomplete" : "Final"),
        ]
      : [
          money(totals.authorized),
          money(totals.projected),
          money(totals.actual),
          anyIncomplete ? { kind: "empty" } : money(totals.variance),
          text(anyIncomplete ? "Actuals incomplete" : "Final"),
        ],
  });

  if (input.contingencyCents) {
    rows.splice(rows.length - 1, 0, {
      label: "Contingency (held at project level)",
      note: "Authorized, but not attached to any item until it is spent.",
      values: detailed
        ? [{ kind: "empty" }, money(input.contingencyCents), { kind: "empty" }, { kind: "empty" }, { kind: "empty" }, { kind: "empty" }, text("Unspent")]
        : [money(input.contingencyCents), { kind: "empty" }, { kind: "empty" }, { kind: "empty" }, text("Unspent")],
    });
    totals.authorized += input.contingencyCents;
    const total = rows[rows.length - 1];
    total.values = detailed
      ? [
          money(totals.baseline),
          money(totals.authorized),
          money(totals.proposed),
          money(totals.actual),
          money(totals.projected),
          anyIncomplete ? { kind: "empty" } : money(totals.variance),
          text(anyIncomplete ? "Actuals incomplete" : "Final"),
        ]
      : [
          money(totals.authorized),
          money(totals.projected),
          money(totals.actual),
          anyIncomplete ? { kind: "empty" } : money(totals.variance),
          text(anyIncomplete ? "Actuals incomplete" : "Final"),
        ];
  }

  const warnings: string[] = [];
  const incompleteCount = items.filter((item) => item.incomplete).length;
  if (incompleteCount > 0) {
    warnings.push(
      `${incompleteCount} item(s) still have incomplete actual costs. Their projected figure is part estimate, and no final variance is claimed for them.`,
    );
  }

  return {
    id: `financial-control-${layout}`,
    title: "Financial control",
    subtitle: `By ${input.dimension} — ${detailed ? "detailed" : "summary"}`,
    identity: input.identity,
    optionSummary: `By ${input.dimension} · ${detailed ? "detailed" : "summary"} layout`,
    sections: [{ title: `By ${input.dimension}`, columnLabel: "Item", columns, rows }],
    warnings,
    disclosures: [
      "Authorized is the baseline plus approved change orders. Projected is actual where the work is finished and estimate where it is not (Phase 9C completeness rules).",
      "Variance compares the projected final with what was authorized; it is left blank while the actual costs of an item are still incomplete.",
      d.rehab.hasBaseline
        ? "Items added after the baseline show no baseline figure — they are new scope, authorized only through a change order."
        : "No baseline has been approved, so nothing on this project is authorized yet.",
    ],
    generatedAt: input.generatedAt ?? new Date().toISOString(),
  };
}

export interface CloseoutExtras {
  identity?: ReportIdentity;
  /** Biggest movers between the current estimate and the projected final. */
  variances?: ProjectedBasisLine[];
  /** Items purchased beyond the requirement, with the excess quantity. */
  overbuy?: { productName: string; excessUnits: number; unit: string; spentCents: Cents }[];
  documents?: { attached: number; missing: number; missingCents: Cents };
  acquisitionLines?: { description: string; amountCents: Cents; credited: boolean }[];
  generatedAt?: string;
}

/**
 * CLOSEOUT — the flagship report: the whole project on a few pages, ending
 * with how it performed against what was authorized and what is still
 * outstanding. Deterministic facts only; there is no success score.
 */
export function fullCloseoutReport(d: ProjectFinancialDashboard, extras: CloseoutExtras = {}): ReportModel {
  const base = closeoutReport(d);
  const sections: ReportSection[] = [...base.sections];

  if (extras.acquisitionLines && extras.acquisitionLines.length > 0) {
    const index = sections.findIndex((section) => section.title === "Total project cost");
    const acquisition: ReportSection = {
      title: "Acquisition detail",
      columnLabel: "Cost",
      columns: ["Amount"],
      rows: [
        { label: "Purchase price", values: [money(d.purchasePriceCents)] },
        ...extras.acquisitionLines.map((line) => ({
          label: line.description,
          note: line.credited ? "Credited at closing — adds nothing to the cost." : undefined,
          values: [money(line.credited ? 0 : line.amountCents)],
        })),
        {
          label: "Total acquisition cost",
          values: [money(d.purchasePriceCents + d.acquisitionCostsCents)],
          emphasis: "subtotal" as const,
        },
      ],
    };
    sections.splice(index < 0 ? sections.length : index, 0, acquisition);
  }

  if (extras.variances && extras.variances.length > 0) {
    const movers = [...extras.variances]
      .filter((line) => line.projectedCents !== line.proposedCents)
      .sort((a, b) => Math.abs(b.projectedCents - b.proposedCents) - Math.abs(a.projectedCents - a.proposedCents))
      .slice(0, 12);
    if (movers.length > 0) {
      sections.push({
        title: "Largest variances",
        note: "Where the projected final differs most from the current estimate.",
        columnLabel: "Item",
        columns: ["Estimate", "Projected", "Difference"],
        rows: movers.map((line) => ({
          label: `${line.scopeLabel ? `${line.scopeLabel} · ` : ""}${line.featureName}`,
          note: line.explanation,
          values: [money(line.proposedCents), money(line.projectedCents), money(line.projectedCents - line.proposedCents)],
        })),
      });
    }
  }

  if (extras.overbuy && extras.overbuy.length > 0) {
    sections.push({
      title: "Quantity purchased beyond the requirement",
      note: "Material bought above what the scope required. Reported as quantity, never merged into the dollar variance.",
      columnLabel: "Product",
      columns: ["Excess", "Recorded cost"],
      rows: extras.overbuy.map((row) => ({
        label: row.productName,
        values: [text(`${row.excessUnits} ${row.unit}`), money(row.spentCents)],
      })),
    });
  }

  if (extras.documents) {
    sections.push({
      title: "Source-document completeness",
      columnLabel: "",
      columns: ["Count", "Amount"],
      rows: [
        {
          label: "Transactions with a document",
          values: [{ kind: "count", value: extras.documents.attached }, { kind: "empty" }],
        },
        {
          label: "Transactions without a document",
          values: [{ kind: "count", value: extras.documents.missing }, money(extras.documents.missingCents)],
          emphasis: extras.documents.missing > 0 ? ("warning" as const) : undefined,
        },
      ],
    });
  }

  return {
    ...base,
    id: "closeout-full",
    identity: extras.identity,
    warnings: d.warnings.map((w) => `${w.label} — ${w.detail}`),
    disclosures: [
      ...base.disclosures,
      "Performance is reported as concrete measures — variance against the authorized budget, profit, ROI, days held. Berrier Reno does not produce a composite success score.",
    ],
    sections,
    generatedAt: extras.generatedAt ?? base.generatedAt,
  };
}

/**
 * THE PROJECT EXECUTION & FINANCIAL FORECAST (spec §75T).
 *
 * The chronology as a document: the phases, the money each one causes,
 * the loan's own events, where cash goes lowest and why — with actuals
 * and projections always distinguishable on the page.
 */
export function executionForecastReport(
  d: ProjectFinancialDashboard,
  result: {
    forecast: {
      events: Array<{
        date: string; title: string; detail: string; amountCents: Cents;
        cashAfterCents: Cents; basis: string; kind: string;
      }>;
      phases: Array<{ label: string; start: string; finish: string; budgetCents: Cents | null; actualCents: Cents; complete: boolean }>;
      assumptions: string[];
      lowestCash: { date: string; cashCents: Cents } | null;
      peakOwnerCashCents: Cents;
      renovationCompleteDate: string | null;
      exitDate: string | null;
    };
    observations: Array<{ severity: string; title: string; detail: string }>;
    strategy: "flip" | "rental";
  },
  identity?: ReportIdentity,
): ReportModel {
  const { forecast } = result;
  const BASIS: Record<string, string> = {
    actual: "Actual",
    high: "Expected",
    assumption: "Planning assumption",
    scenario: "Scenario",
  };

  const phaseRows: ReportRow[] = forecast.phases
    .filter((p) => p.start && p.finish)
    .map((phase) => ({
      label: phase.label,
      note: phase.complete ? "Complete" : undefined,
      values: [
        text(`${phase.start} → ${phase.finish}`),
        phase.budgetCents === null ? text("no renovation budget") : money(phase.budgetCents),
        phase.actualCents === 0 ? text("—") : money(phase.actualCents),
      ],
    }));

  const moneyRows: ReportRow[] = forecast.events
    .filter((e) => e.amountCents !== 0)
    .map((event) => ({
      label: `${event.date} — ${event.title}`,
      note: event.detail || undefined,
      values: [money(event.amountCents), money(event.cashAfterCents), text(BASIS[event.basis] ?? event.basis)],
    }));

  const milestoneRows: ReportRow[] = [
    { label: "Renovation complete", values: [text(forecast.renovationCompleteDate ?? "not scheduled")] },
    {
      label: result.strategy === "flip" ? "Expected sale" : "Expected stabilisation",
      values: [text(forecast.exitDate ?? "not scheduled")],
    },
    { label: "Cash needed at its worst", values: [money(forecast.peakOwnerCashCents)], emphasis: "total" },
    ...(forecast.lowestCash
      ? [{
          label: "Lowest projected cash",
          note: forecast.lowestCash.date,
          values: [money(forecast.lowestCash.cashCents)],
        } as ReportRow]
      : []),
  ];

  const sections: ReportSection[] = [
    { title: "Where the project ends up", columns: ["Amount"], rows: milestoneRows },
    { title: "Construction phases", columns: ["When", "Budget", "Spent"], rows: phaseRows },
    { title: "Money, in order", columns: ["In / out", "Cash after", "Basis"], rows: moneyRows },
  ];
  if (result.observations.length > 0) {
    sections.push({
      title: "Liquidity and timing to watch",
      columns: [""],
      rows: result.observations.map((observation) => ({
        label: observation.title,
        note: observation.detail,
        values: [{ kind: "empty" }],
        emphasis: observation.severity === "risk" ? "warning" : undefined,
      })),
    });
  }

  return {
    id: "execution-forecast",
    title: "Project execution & financial forecast",
    subtitle: `JOB ${d.jobNumber} — ${d.projectName} · ${result.strategy === "flip" ? "Flip" : "Rental"}`,
    identity,
    sections,
    disclosures: [
      "Rows marked Actual are recorded facts. Expected rows follow from the committed schedule and confirmed loan terms. Planning-assumption rows depend on the assumptions listed below, which nobody has contractually promised.",
      ...forecast.assumptions,
      "The schedule decides when work happens, the category budget decides what it costs, and the loan's own ledger decides every balance and interest figure — this document rearranges those, it does not recalculate them.",
    ],
    generatedAt: new Date().toISOString(),
  };
}
